import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Main as Container } from 'Components/Main';
import styled from 'styled-components';
import {
  CheckCircleIcon, CheckboxBlankIcon, CheckboxIndeterminateIcon,
} from 'Components/common/Icons';
import { editPolicyVersionStatus, getPolicyById } from 'services/policies-service';
import { useState } from 'react';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';
import { useSelector } from 'react-redux';
import { useAuthUser } from 'store/auth';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { useTranslation } from 'react-i18next';
import { Dialog, PageHeader } from 'Components/ui';

const StyledTable = styled.table`
  width: 100%;
  min-width: 650px;
  border-collapse: collapse;
`
const StyledTh = styled.th`
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid var(--vo-cloud);
  font-weight: 600;
  font-size: 14px;
`
const StyledTd = styled.td`
  padding: 12px;
  border-bottom: 1px solid var(--vo-cloud);
  font-size: 14px;
  vertical-align: middle;
`
const TableWrapper = styled.div`
  overflow-x: auto;
  margin-bottom: 1rem;
  margin-top: 1rem;
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`

export const PoliciesChecklistVersion = () => {
  const { t } = useTranslation(['policy']);
  const { id, idVersion } = useParams();
  const user = useSelector(useAuthUser);
  const [refetch, serRefetch] = useState(0);
  const {
    isLoading,
    error,
    data: policy,
  } = useQuery(['getPolicyById', refetch], async () => {
    const resPolicy = await getPolicyById(id);

    if (resPolicy.status === 200) return resPolicy.data;

    return null;
  });

  const updateStatus = async status => {
    try {
      Dialog.loading({ title: 'Loading...' });
      await editPolicyVersionStatus(id, idVersion, status);
      Dialog.close();
      Dialog.fire({
        title: t('policy:checklist.swal.successTitle'),
        text: t('policy:checklist.swal.successText'),
        icon: 'success',
        willClose: () => {
          serRefetch(refetch + 1);
        },
      });
    } catch (error) {
      Dialog.alert({
        title: t('policy:checklist.swal.errorTitle'),
        message: error.response.data.message,
        icon: 'error',
      });
    }
  };

  const getStatusReview = policyVersion => {
    if (!policyVersion.reviewedDate && policyVersion.reviewer._id !== user.userId) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <CheckboxIndeterminateIcon fill={'var(--vo-danger)'} />
          &nbsp;{t('policy:checklist.notReviewer')}
        </div>
      );
    } else if (policyVersion.reviewedDate) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <CheckCircleIcon fill={'var(--vo-success)'} />
          &nbsp;{t('policy:checklist.reviewed')}
        </div>
      );
    }
    return (
      <div style={{ verticalAlign: 'middle', display: 'flex' }}>
        <span style={{ display: 'inline-flex', cursor: 'pointer' }} onClick={() => updateStatus('Revisado')}>
          <CheckboxBlankIcon fill={'var(--color-brand-live)'} />
        </span>
        &nbsp;{t('policy:checklist.registerReview')}
      </div>
    );
  };

  const getStatusApprove = policyVersion => {
    if (!policyVersion.reviewedDate) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <CheckboxIndeterminateIcon fill={'var(--vo-danger)'} />
          &nbsp;{t('policy:checklist.waitingReview')}
        </div>
      );
    } else if (policyVersion.reviewedDate && user.userId !== policyVersion.approver._id) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <CheckboxIndeterminateIcon fill={'var(--vo-danger)'} />
          &nbsp;{t('policy:checklist.notApprover')}
        </div>
      );
    } else if (policyVersion.approvedDate) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <CheckCircleIcon fill={'var(--vo-success)'} />
          &nbsp;{t('policy:checklist.approved')}
        </div>
      );
    }
    return (
      <div style={{ verticalAlign: 'middle', display: 'flex' }}>
        <span style={{ display: 'inline-flex', cursor: 'pointer' }} onClick={() => updateStatus('Aprovado')}>
          <CheckboxBlankIcon fill={'var(--color-brand-live)'} />
        </span>
        &nbsp;{t('policy:checklist.approve')}
      </div>
    );
  };

  const getStatusPublish = policyVersion => {
    if (!policyVersion.approvedDate) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <CheckboxIndeterminateIcon fill={'var(--vo-danger)'} />
          &nbsp;{t('policy:checklist.waitingApproval')}
        </div>
      );
    } else if (policyVersion.publishedDate && user.userId !== policyVersion.author._id) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <CheckboxIndeterminateIcon fill={'var(--vo-danger)'} />
          &nbsp;{t('policy:checklist.notPublisher')}
        </div>
      );
    } else if (policyVersion.publishedDate) {
      return (
        <div style={{ verticalAlign: 'middle', display: 'flex' }}>
          <CheckCircleIcon fill={'var(--vo-success)'} />
          &nbsp;{t('policy:checklist.published')}
        </div>
      );
    }
    return (
      <div style={{ verticalAlign: 'middle', display: 'flex' }}>
        <span style={{ display: 'inline-flex', cursor: 'pointer' }} onClick={() => updateStatus('Publicado')}>
          <CheckboxBlankIcon fill={'var(--color-brand-live)'} />
        </span>
        &nbsp;{t('policy:checklist.publish')}
      </div>
    );
  };
  return (
    <>
      {!isLoading && !error && (
        <Container>
          <PageHeader
            title={`${policy?.name} - Versão (${policy?.policyVersions[idVersion].version})`}
            segments={[
              { label: t('common:breadcrumbs.compliance') },
              { label: t('policy:page.title'), route: '/policies' },
              { label: policy?.name, route: `/policies/show/${id}` },
              { label: t('policy:versions.steps.versions'), route: `/policies/${id}/versions` },
            ]}
          />
          <div className="container" style={{ width: '100%' }}>
            <TableWrapper>
              <StyledTable aria-label="simple table">
                <thead>
                  <tr key={'table-row-header'}>
                    <StyledTh key={`thrc-stage`}>{t('policy:checklist.stage.label')}</StyledTh>
                    <StyledTh key={`thrc-status`}>{t('policy:checklist.status.label')}</StyledTh>
                    <StyledTh key={`thrc-date`}>{t('policy:checklist.date.label')}</StyledTh>
                    <StyledTh key={`thrc-sponsor`}>{t('policy:checklist.sponsor.label')}</StyledTh>
                  </tr>
                </thead>
                <tbody>
                  <tr key={'table-row-header-review'}>
                    <StyledTd key={`thrc-stage-1`}>{t('policy:checklist.stage.review')}</StyledTd>
                    <StyledTd key={`thrc-status-1`}>{getStatusReview(policy?.policyVersions[idVersion])}</StyledTd>
                    <StyledTd key={`thrc-date-1`}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion].reviewedDate)}
                    </StyledTd>
                    <StyledTd key={`thrc-sponsor-1`}>{policy?.policyVersions[idVersion].reviewer?.name}</StyledTd>
                  </tr>
                  <tr key={'table-row-header-approve'}>
                    <StyledTd key={`thrc-stage-2`}>{t('policy:checklist.stage.approval')}</StyledTd>
                    <StyledTd key={`thrc-status-2`}>{getStatusApprove(policy?.policyVersions[idVersion])}</StyledTd>
                    <StyledTd key={`thrc-date-2`}>
                      {formatDateTimeToDateTimeBR(policy?.policyVersions[idVersion].approvedDate)}
                    </StyledTd>
                    <StyledTd key={`thrc-sponsor-2`}>{policy?.policyVersions[idVersion].approver?.name}</StyledTd>
                  </tr>
                  <tr key={'table-row-header-publish'}>
                    <StyledTd key={`thrc-stage-3`}>{t('policy:checklist.stage.publication')}</StyledTd>
                    <StyledTd key={`thrc-status-3`}>{getStatusPublish(policy?.policyVersions[idVersion])}</StyledTd>
                    <StyledTd key={`thrc-date-3`}>
                      {policy?.policyVersions[idVersion]?.publishedDate ? formatDateTimeToDateTimeBR(policy.policyVersions[idVersion].publishedDate) : t('policy:checklist.notPublishedYet')}
                    </StyledTd>
                    <StyledTd key={`thrc-sponsor-3`}>{policy?.policyVersions[idVersion].author?.name}</StyledTd>
                  </tr>
                </tbody>
              </StyledTable>
            </TableWrapper>
          </div>
        </Container>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>Erro</div>}
    </>
  );
};
